import {
  AssetFamilySearchFilterEnum,
  AssetTypeSearchFilterEnum,
  FileTypeSearchFilterEnum,
  MediaTypeSearchFilterEnum,
} from 'app/domain';

/**
 * A route this app mounts, and the search its path states.
 *
 * `staticParams` is what the path says that the query never will: the segments
 * are matched literally, so without it the parameter is unstated and whatever
 * default sits behind it answers instead. Federation merges it into the
 * `params` the route's middleware, loader and queries receive.
 */
export type MountedRoute = {
  path: string;
  /** The name this route reports itself as to tracking. */
  pageName: 'search' | 'editorial';
  staticParams?: Readonly<Record<string, string>>;
};

export const SEARCH_CONTROLLER_PATHS: readonly MountedRoute[] = [
  // The asset type is in the path, not the query, and the segment is matched
  // literally — so unless the route says what it means the parameter is never
  // stated and the schema default answers instead. The spelling is not the
  // value: `image-film` is `image,film`, `video-template` is `videotemplate`.
  {
    pageName: 'search',
    path: 'search/2/image',
    staticParams: { assettype: AssetTypeSearchFilterEnum.IMAGE },
  },
  {
    pageName: 'search',
    path: 'search/2/film',
    staticParams: { assettype: AssetTypeSearchFilterEnum.FILM },
  },
  {
    pageName: 'search',
    path: 'search/2/image-film',
    staticParams: { assettype: AssetTypeSearchFilterEnum.IMAGE_AND_FILM },
  },
  {
    pageName: 'search',
    path: 'search/2/video-template',
    staticParams: { assettype: AssetTypeSearchFilterEnum.VIDEO_TEMPLATE },
  },
  // These carry an id in the path rather than a parameter value. What that id
  // means to a search is not substitution — a curated set also fixes the
  // family, and more-like-this resolves family and asset type from the asset
  // itself — so none of them is a declaration's to make.
  { path: 'search/more-like-this/:id', pageName: 'search' },
  { path: 'search/sets/:id', pageName: 'search' },
  { path: 'sets/:id/:title', pageName: 'search' },
  { path: 'search/stack/:id', pageName: 'search' },
  { path: 'search/recently-viewed', pageName: 'search' },
  { path: 'search/search-by-image', pageName: 'search' },
  { path: 'portfolio/:contributorName', pageName: 'search' },
];

// EditorialLandingController#{show,show_parent_event} plus
// search#event_search_seo — all share the editorial-* prefixes, so a splat per
// prefix covers every editorial SRP route (landing, category, collection, events).
// An editorial prefix names the family as well as the asset type. Every action
// these paths reach settles it: the landing pages force it as an override, and
// the event and child-event searches declare it alongside the asset type, the
// same way the asset type itself is declared.
const EDITORIAL = {
  assettype: AssetTypeSearchFilterEnum.IMAGE,
  family: AssetFamilySearchFilterEnum.EDITORIAL,
} as const;

export const EDITORIAL_PATHS: readonly MountedRoute[] = [
  {
    pageName: 'editorial',
    path: 'editorial-images/*',
    staticParams: EDITORIAL,
  },
  {
    pageName: 'editorial',
    path: 'editorial-footage/*',
    staticParams: { ...EDITORIAL, assettype: AssetTypeSearchFilterEnum.FILM },
  },
  {
    pageName: 'editorial',
    path: 'editorial-video/*',
    staticParams: { ...EDITORIAL, assettype: AssetTypeSearchFilterEnum.FILM },
  },
];

// [GATEWAY] GatewayController#category — localized front doors. One Rails
// action serves all three, but each declares a different search: a photo slug
// only fixes the asset type, an illustration slug fixes the family and media
// type too, and a vector slug narrows that again by file type. They were one
// list here while the path implied nothing; now that each declares what it
// means, they are three.
//
// Slug sets are the Rails-route ∩ haproxy-allowlist intersection — see the
// source-of-truth note above. Where a word is ambiguous between illustrations
// and vectors, Rails resolves it as an illustration (its routes are declared
// first), and so do these.
const GATEWAY_PHOTO_SLUGS = [
  'bức-ảnh',
  'fotky',
  'foto',
  'foto-foto',
  'fotografier',
  'fotografii',
  'foton',
  'fotos',
  'fotoğraflar',
  'fotók',
  'immagine',
  'obrazy',
  'photos',
  'valokuvat',
  'фотографии',
  'фотографії',
  'ภาพถ่าย',
  '写真',
  '圖片',
  '사진',
  '이미지',
];

const GATEWAY_ILLUSTRATION_SLUGS = [
  'grafiken',
  'hình-minh-họa',
  'illustraties',
  'illustrationen',
  'illustrationer',
  'illustrations',
  'illustrazioni',
  'illusztrációk',
  'illüstrasyon',
  'illüstrasyonlar',
  'ilustrace',
  'ilustraciones',
  'ilustracje',
  'ilustrasi',
  'ilustrações',
  'ilustrații',
  'kuvitukset',
  'иллюстрации',
  'ілюстрації',
  'ภาพประกอบ',
  'イラスト',
  '插圖',
  '일러스트',
];

const GATEWAY_VECTOR_SLUGS = [
  'artes-vetoriais',
  'grafiki-wektorowe',
  'grafis-vektor',
  'vecteurs',
  'vectorbestanden',
  'vectores',
  'vectori',
  'vectoriels',
  'vectors',
  'vektorer',
  'vektorgrafiken',
  'vektorit',
  'vektoros-alkotások',
  'vektory',
  'vektörler',
  'vetores',
  'vettori',
  'véc-tơ',
  'векторне-мистецтво',
  'векторные-файлы',
  'ภาพเวกเตอร์',
  'ベクター',
  'ベクトルアート',
  '向量',
  '向量圖形',
  '벡터',
  '벡터-아트',
];

/**
 * The category front doors, each declaring the search its slug names.
 *
 * A photo slug fixes only the asset type. An illustration slug fixes the
 * family and media type with it. A vector slug is an illustration narrowed to
 * one file type — which is why the three cannot share a list any more: they
 * share a Rails action and a path shape, not a meaning.
 */
export const GATEWAY_CATEGORY_ROUTES: readonly MountedRoute[] = [
  ...GATEWAY_PHOTO_SLUGS.map((slug) => ({
    pageName: 'search' as const,
    path: `${slug}/:category`,
    staticParams: { assettype: AssetTypeSearchFilterEnum.IMAGE },
  })),
  ...GATEWAY_ILLUSTRATION_SLUGS.map((slug) => ({
    pageName: 'search' as const,
    path: `${slug}/:category`,
    staticParams: {
      assettype: AssetTypeSearchFilterEnum.IMAGE,
      family: AssetFamilySearchFilterEnum.CREATIVE,
      mediatype: MediaTypeSearchFilterEnum.ILLUSTRATION,
    },
  })),
  ...GATEWAY_VECTOR_SLUGS.map((slug) => ({
    pageName: 'search' as const,
    path: `${slug}/:category`,
    staticParams: {
      assettype: AssetTypeSearchFilterEnum.IMAGE,
      family: AssetFamilySearchFilterEnum.CREATIVE,
      mediatype: MediaTypeSearchFilterEnum.ILLUSTRATION,
      assetfiletype: FileTypeSearchFilterEnum.EPS,
    },
  })),
];

// [GATEWAY] GatewayController#video_category — localized video front doors
// (plural + singular forms). Renders the gateway SRP. Slug set is the
// Rails-route ∩ haproxy-allowlist intersection — see the source-of-truth
// note above.
const GATEWAY_VIDEO_SLUGS = [
  'film',
  'klip-video',
  'phim-ảnh-sẵn-có',
  'videa',
  'video',
  'videolar',
  'videor',
  'videos',
  'videot',
  'videouri',
  'videók',
  'vidéo',
  'vidéos',
  'vídeo',
  'vídeos',
  'wideo',
  'видео-клипы',
  'відео-кліп',
  'คลิปวีดีโอ',
  '動画',
  '影片檔',
  '映像素材',
  '비디오클립',
];

export const GATEWAY_VIDEO_ROUTES: readonly MountedRoute[] =
  GATEWAY_VIDEO_SLUGS.map((slug) => ({
    pageName: 'search' as const,
    path: `${slug}/:videocategory`,
    staticParams: { assettype: AssetTypeSearchFilterEnum.FILM },
  }));

/**
 * Every route this app mounts, in one list because the tree renders them the
 * same way. What distinguishes them is what each declares, which is the tables
 * above — not how they are mounted, which is `AppRoutes`.
 */
export const MOUNTED_ROUTES: readonly MountedRoute[] = [
  ...SEARCH_CONTROLLER_PATHS,
  ...EDITORIAL_PATHS,
  ...GATEWAY_CATEGORY_ROUTES,
  ...GATEWAY_VIDEO_ROUTES,
];
